import { FormEvent, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { IDeviceBrandValueLabel, IESimDeviceForm } from "@features/cart/interfaces/IESimDeviceForm";
import { IDeviceDetailsResponse } from "@features/cart/interfaces/IDeviceDetails";
import { getObjectByPath } from "@utils/getObjectByPath";

interface IParams {
    offerId: number;
    setIsESimFormOpen: (isESimFormOpen: boolean) => void;
    otherChoice: IDeviceBrandValueLabel;
    deviceDetails: IDeviceDetailsResponse | undefined;
    postDeviceDetails: (offerId: number, formData: IESimDeviceForm) => Promise<unknown>;
    deleteDeviceDetails: (offerId: number) => Promise<unknown>;
}

export const useESimDeviceForm = (props: IParams) => {
    const { offerId, deviceDetails, postDeviceDetails, deleteDeviceDetails, setIsESimFormOpen, otherChoice } = props;

    const { control, getValues, setValue, trigger } = useFormContext();

    const namePrefix = useMemo(() => `eSim.${offerId}.`, [offerId]);
    const brandFieldName = useMemo(() => `${namePrefix}brand`, [namePrefix]);
    const eidFieldName = useMemo(() => `${namePrefix}eid`, [namePrefix]);

    const watchBrand = useWatch<string>({ name: brandFieldName, control: control, defaultValue: "" });

    const isBrandSelected = useMemo(
        () => watchBrand !== "" && watchBrand.toLowerCase() !== otherChoice.value.toLowerCase(),
        [otherChoice.value, watchBrand]
    );
    const isOtherChoice = useMemo(() => watchBrand.toLowerCase() === otherChoice.value.toLowerCase(), [
        otherChoice.value,
        watchBrand,
    ]);

    useEffect(() => {
        if (deviceDetails) {
            setValue(brandFieldName, deviceDetails.manufacturer);

            return;
        }

        const currentBrand = getValues(brandFieldName);
        const currentEid = getValues(eidFieldName);

        if (currentBrand === undefined) {
            setValue(brandFieldName, "");
        }
        if (currentEid === undefined) {
            setValue(eidFieldName, "");
        }
    }, [brandFieldName, deviceDetails, eidFieldName, getValues, setValue]);

    useEffect(() => {
        if (!deviceDetails) {
            return;
        }
        if (!isBrandSelected) {
            return;
        }

        setValue(eidFieldName, deviceDetails.eid);
    }, [deviceDetails, eidFieldName, isBrandSelected, setValue]);

    const onCancel = () => setIsESimFormOpen(false);

    // Validates only the eSim fields of this offer to avoid triggering the global
    // FormProvider validation (which would also flag broadband/portIn sections).
    // The EID field is only registered when a real brand is selected (the Controller
    // is rendered conditionally), so we must not request validation on it otherwise
    // (Other / empty brand) since trigger returns false on unregistered fields.
    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const fieldsToValidate = isBrandSelected ? [brandFieldName, eidFieldName] : [brandFieldName];
        const isValid = await trigger(fieldsToValidate);
        if (!isValid) {
            return;
        }

        const formValues = getValues();
        const formData =
            getObjectByPath<IESimDeviceForm>(formValues, `eSim.${offerId}`) ??
            ({ brand: "", eid: "" } as IESimDeviceForm);

        if (isBrandSelected && deviceDetails === undefined) {
            await postDeviceDetails(offerId, formData);
            setIsESimFormOpen(false);

            return;
        }

        if (isBrandSelected && deviceDetails) {
            await deleteDeviceDetails(offerId);
            await postDeviceDetails(offerId, formData);
            setIsESimFormOpen(false);

            return;
        }

        if (isOtherChoice && deviceDetails) {
            await deleteDeviceDetails(offerId);
            setIsESimFormOpen(false);

            return;
        }

        setIsESimFormOpen(false);
    };

    return {
        brandFieldName,
        eidFieldName,
        isBrandSelected,
        isOtherChoice,
        onCancel,
        onSubmit,
    };
};
